var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a, _b;
import $ from 'jquery';
import _ from 'lodash';
import { PUZZLE_PAGE_NONE, ViewType, } from '@cdo/apps/types/progressTypes';
import { createSlice, } from '@reduxjs/toolkit';
import { processServerStudentProgress, getLevelResult, } from '@cdo/apps/templates/progress/progressHelpers';
import { mergeActivityResult } from './activityUtils';
import { SET_VIEW_TYPE } from './viewAsRedux';
import { setVerified } from '@cdo/apps/code-studio/verifiedInstructorRedux';
import { authorizeLockable } from './lessonLockRedux';
import { updateBrowserForLevelNavigation } from './browserNavigation';
import { TestResults } from '@cdo/apps/constants';
import { nextLevelId } from './progressReduxSelectors';
var initialState = {
    currentLevelId: null,
    // These first fields never change after initialization.
    currentLessonId: undefined,
    deeperLearningCourse: null,
    // used on multi-page assessments
    saveAnswersBeforeNavigation: null,
    lessons: null,
    lessonGroups: null,
    scriptId: null,
    scriptName: null,
    scriptDisplayName: undefined,
    unitTitle: null,
    courseId: null,
    isLessonExtras: false,
    // The remaining fields do change after initialization.
    // unitProgress is of type unitProgressType (a map of levelId ->
    // studentLevelProgressType)
    unitProgress: {},
    unitProgressHasLoaded: false,
    // note: eventually, we expect usage of this field to be replaced with unitProgress
    levelResults: {},
    focusAreaLessonIds: [],
    peerReviewLessonInfo: null,
    peerReviewsPerformed: [],
    postMilestoneDisabled: false,
    isAge13Required: false,
    // Do students see summary view by default?
    studentDefaultsSummaryView: true,
    isSummaryView: true,
    isMiniView: false,
    hasFullProgress: false,
    lessonExtrasEnabled: false,
    // Note: usingDbProgress === "user is logged in". However, it is
    // possible that we can get the user progress back from the DB
    // prior to having information about the user login state.
    // TODO: Use sign in state to determine where to source user progress from
    usingDbProgress: false,
    currentPageNumber: PUZZLE_PAGE_NONE,
    courseVersionId: undefined,
    unitDescription: undefined,
    unitStudentDescription: undefined,
    changeFocusAreaPath: undefined,
    unitCompleted: undefined,
};
var progressSlice = createSlice({
    name: 'progress',
    initialState: initialState,
    reducers: {
        initProgress: function (state, action) {
            var lessons = action.payload.lessons;
            // Re-initializing with full set of lessons shouldn't blow away currentLessonId
            var currentLessonId = state.currentLessonId ||
                (lessons.length === 1 ? lessons[0].id : undefined);
            state.currentLevelId || (state.currentLevelId = action.payload.currentLevelId);
            state.deeperLearningCourse = action.payload.deeperLearningCourse;
            state.saveAnswersBeforeNavigation =
                action.payload.saveAnswersBeforeNavigation;
            state.lessons = processedLessons(lessons, action.payload.deeperLearningCourse);
            state.lessonGroups = action.payload.lessonGroups;
            state.peerReviewLessonInfo = action.payload.peerReviewLessonInfo;
            state.scriptId = action.payload.scriptId;
            state.scriptName = action.payload.scriptName;
            state.scriptDisplayName = action.payload.scriptDisplayName;
            state.unitTitle = action.payload.unitTitle;
            state.unitDescription = action.payload.unitDescription;
            state.unitStudentDescription = action.payload.unitStudentDescription;
            state.courseId = action.payload.courseId;
            state.courseVersionId = action.payload.courseVersionId;
            state.currentLessonId = currentLessonId;
            state.hasFullProgress = action.payload.isFullProgress;
            state.isLessonExtras = action.payload.isLessonExtras;
            state.currentPageNumber = action.payload.currentPageNumber;
        },
        setCurrentLevelId: function (state, action) {
            state.currentLevelId = action.payload;
        },
        setScriptProgress: function (state, action) {
            state.unitProgress = processServerStudentProgress(action.payload);
            state.unitProgressHasLoaded = true;
        },
        clearResults: function (state) {
            state.levelResults = initialState.levelResults;
        },
        useDbProgress: function (state) {
            state.usingDbProgress = true;
        },
        mergeResults: function (state, action) {
            var newLevelResults = {};
            var combinedLevels = Object.keys(__assign(__assign({}, state.levelResults), action.payload));
            combinedLevels.forEach(function (key) {
                var levelId = parseInt(key);
                newLevelResults[levelId] = mergeActivityResult(state.levelResults[levelId], action.payload[levelId]);
            });
            state.levelResults = newLevelResults;
        },
        overwriteResults: function (state, action) {
            state.levelResults = action.payload;
        },
        mergePeerReviewProgress: function (state, action) {
            if (state.peerReviewLessonInfo) {
                state.peerReviewLessonInfo = __assign(__assign({}, state.peerReviewLessonInfo), { levels: state.peerReviewLessonInfo.levels.map(function (level, index) { return (__assign(__assign({}, level), action.payload[index])); }) });
            }
        },
        updateFocusArea: {
            reducer: function (state, action) {
                state.changeFocusAreaPath = action.payload.changeFocusAreaPath;
                state.focusAreaLessonIds = action.payload.focusAreaLessonIds;
            },
            prepare: function (changeFocusAreaPath, focusAreaLessonIds) {
                return {
                    payload: {
                        changeFocusAreaPath: changeFocusAreaPath,
                        focusAreaLessonIds: focusAreaLessonIds,
                    },
                };
            },
        },
        disablePostMilestone: function (state) {
            state.postMilestoneDisabled = true;
        },
        setIsAge13Required: function (state, action) {
            state.isAge13Required = action.payload;
        },
        setIsSummaryView: function (state, action) {
            state.isSummaryView = action.payload;
        },
        setIsMiniView: function (state, action) {
            state.isMiniView = action.payload;
        },
        setStudentDefaultsSummaryView: function (state, action) {
            state.studentDefaultsSummaryView = action.payload;
        },
        setCurrentLessonId: function (state, action) {
            // if we already have a currentLessonId, that means we're on a puzzle page,
            // and we want currentLessonId to remain the same (rather than reflecting
            // the last lesson the user has made progress on).
            if (!state.currentLessonId) {
                state.currentLessonId = action.payload;
            }
        },
        setScriptCompleted: function (state) {
            state.unitCompleted = true;
        },
        setLessonExtrasEnabled: function (state, action) {
            state.lessonExtrasEnabled = action.payload;
        },
    },
    extraReducers: (_a = {},
        // TODO: When we convert viewAsRedux to redux-toolkit, we will need to use
        // createAction there instead of referencing the string here.
        _a[SET_VIEW_TYPE] = function (state, action) {
            state.isSummaryView =
                action.viewType === ViewType.Participant &&
                    state.studentDefaultsSummaryView;
        },
        _a),
});
export var queryUserProgress = function (userId) {
    return function (dispatch, getState) {
        var state = getState().progress;
        return userProgressFromServer(state, dispatch, userId);
    };
};
// The user has navigated to a new level in the current lesson,
// so we should update the browser and also set this as the new
// current level.
export function navigateToLevelId(levelId) {
    return function (dispatch, getState) {
        var state = getState().progress;
        if (!state.currentLessonId) {
            return;
        }
        var newLevel = getLevelById(state.lessons, state.currentLessonId, levelId);
        if (!newLevel) {
            return;
        }
        updateBrowserForLevelNavigation(state, newLevel.url, levelId);
        dispatch(setCurrentLevelId(levelId));
    };
}
// Navigate to the next level in the progression, if it exists.
export function navigateToNextLevel() {
    return function (dispatch, getState) {
        var levelId = nextLevelId(getState());
        if (levelId === undefined) {
            return;
        }
        dispatch(navigateToLevelId(levelId));
    };
}
// The user has successfully completed the level and the page
// will not be reloading.
export function sendSuccessReport(appType) {
    return function (dispatch, getState) {
        var state = getState().progress;
        var levelId = state.currentLevelId;
        if (!state.currentLessonId || !levelId) {
            return;
        }
        var currentLevel = getLevelById(state.lessons, state.currentLessonId, levelId);
        if (!currentLevel) {
            return;
        }
        var scriptLevelId = currentLevel.id;
        // The server does not appear to use the user ID parameter,
        // so just pass 0, like some other milestone posts do.
        var userId = 0;
        // An ideal score.
        var idealPassResult = TestResults.ALL_PASS;
        var data = {
            app: appType,
            result: true,
            testResult: idealPassResult,
        };
        fetch("/milestone/".concat(userId, "/").concat(scriptLevelId, "/").concat(levelId), {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(data),
        }).then(function (response) {
            var _a;
            if (response.ok && levelId !== null) {
                // Update the progress store by merging in this
                // particular result immediately.
                dispatch(mergeResults((_a = {}, _a[levelId] = idealPassResult, _a)));
            }
        });
    };
}
// Helpers
/**
 * Requests user progress from the server and dispatches other redux actions
 * based on the server's response data.
 */
var userProgressFromServer = function (state, dispatch, userId) {
    if (userId === void 0) { userId = null; }
    if (!state.scriptName) {
        var message = "Could not request progress for user ID ".concat(userId, " from server: scriptName must be present in progress redux.");
        throw new Error(message);
    }
    // If we have a userId, we can clear any progress in redux and request all progress
    // from the server.
    if (userId) {
        dispatch(clearResults());
    }
    return $.ajax({
        url: "/api/user_progress/".concat(state.scriptName),
        method: 'GET',
        data: { user_id: userId },
    }).done(function (data) {
        if (!data || _.isEmpty(data)) {
            return;
        }
        if (data.isVerifiedInstructor) {
            dispatch(setVerified());
        }
        // We are on an overview page if currentLevelId is undefined.
        var onOverviewPage = !state.currentLevelId;
        // Show lesson plan links and other teacher info if instructor and on unit overview page.
        if ((data.isInstructor || data.teacherViewingStudent) &&
            !data.deeperLearningCourse &&
            onOverviewPage) {
            // Default to summary view if teacher is viewing their student, otherwise default to detail view.
            dispatch(setIsSummaryView(data.teacherViewingStudent));
        }
        if (data.focusAreaLessonIds) {
            dispatch(updateFocusArea(data.changeFocusAreaPath, data.focusAreaLessonIds));
        }
        dispatch(authorizeLockable(data.lockableAuthorized));
        if (data.completed) {
            dispatch(setScriptCompleted());
        }
        // Merge progress from server
        if (data.progress) {
            dispatch(setScriptProgress(data.progress));
            // Note that we set the full progress object above in redux but also set
            // a map containing just level results. This is the legacy code path and
            // the goal is to eventually update all code paths to use unitProgress
            // instead of levelResults.
            var levelResults = _.mapValues(data.progress, getLevelResult);
            dispatch(mergeResults(levelResults));
            if (data.peerReviewsPerformed) {
                dispatch(mergePeerReviewProgress(data.peerReviewsPerformed));
            }
            if (data.current_lesson) {
                dispatch(setCurrentLessonId(data.current_lesson));
            }
        }
    });
};
/**
 * Given an array of lessons, a lesson ID, and a level ID, returns
 * the requested level.
 */
function getLevelById(lessons, lessonId, levelId) {
    var lesson = lessons === null || lessons === void 0 ? void 0 : lessons.find(function (lesson) { return lesson.id === lessonId; });
    if (lesson) {
        return lesson.levels.find(function (level) { return level.ids.find(function (id) { return id === levelId; }); });
    }
}
/**
 * Does some processing of our passed in lesson, namely
 * - Removes 'hidden' field
 * - Adds 'lessonNumber' field for non-PLC lessons which
 * are not lockable or have a lesson plan
 */
export function processedLessons(lessons, isPlc) {
    var numLessonsWithLessonPlan = 0;
    return lessons.map(function (lesson) {
        var lessonNumber;
        if (!isPlc && lesson.numberedLesson) {
            numLessonsWithLessonPlan++;
            lessonNumber = numLessonsWithLessonPlan;
        }
        return __assign(__assign({}, _.omit(lesson, 'hidden')), { lessonNumber: lessonNumber });
    });
}
export var initProgress = (_b = progressSlice.actions, _b.initProgress), setCurrentLevelId = _b.setCurrentLevelId, setScriptProgress = _b.setScriptProgress, clearResults = _b.clearResults, useDbProgress = _b.useDbProgress, mergeResults = _b.mergeResults, overwriteResults = _b.overwriteResults, mergePeerReviewProgress = _b.mergePeerReviewProgress, updateFocusArea = _b.updateFocusArea, disablePostMilestone = _b.disablePostMilestone, setIsAge13Required = _b.setIsAge13Required, setIsSummaryView = _b.setIsSummaryView, setIsMiniView = _b.setIsMiniView, setStudentDefaultsSummaryView = _b.setStudentDefaultsSummaryView, setCurrentLessonId = _b.setCurrentLessonId, setScriptCompleted = _b.setScriptCompleted, setLessonExtrasEnabled = _b.setLessonExtrasEnabled;
export default progressSlice.reducer;
// export private function(s) to expose to unit testing
export var __testonly__ = IN_UNIT_TEST
    ? {
        userProgressFromServer: userProgressFromServer,
    }
    : {};
